import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable, catchError, combineLatest, map, of, shareReplay, take } from 'rxjs';
import { FeatureFlag } from 'src/app/bo/featureFlag';
import { Site } from 'src/app/bo/site';
import { HttpService } from 'src/app/services/http.service';

/** Quand ce flag est actif, l'ancien écran site remplace le nouveau. */
export const LEGACY_SITE_FEATURE_NAME = 'show-old-site-page';

@Injectable({ providedIn: 'root' })
export class FeatureFlagService {
  private readonly http = inject(HttpService);

  private readonly flags$ = this.http.get<FeatureFlag[]>('FeatureFlag').pipe(
    catchError(() => of([] as FeatureFlag[])),
    shareReplay(1),
  );

  /** Organisation de chaque site actif de l'utilisateur. */
  private readonly siteOrganizations$ = this.http.get<Site[]>('Sites/Active').pipe(
    map(sites => new Map(
      sites
        .filter(site => site.siteId != null && site.region?.orgId != null)
        .map(site => [site.siteId, site.region.orgId] as const),
    )),
    catchError(() => of(new Map<number, number>())),
    shareReplay(1),
  );

  private readonly legacySiteState$ = combineLatest([this.flags$, this.siteOrganizations$]).pipe(
    map(([flags, siteOrganizations]) => ({
      flag: flags.find(flag => flag.featureName === LEGACY_SITE_FEATURE_NAME),
      siteOrganizations,
    })),
    shareReplay(1),
  );

  private readonly legacySiteState = toSignal(this.legacySiteState$, { initialValue: null });

  /**
   * Lecture synchrone, pour un composant déjà affiché. Faux tant que l'API n'a pas répondu.
   * Le flag est évalué pour l'organisation du site visé : un utilisateur multi-organisations
   * garde le nouvel écran sur les sites des organisations non concernées.
   */
  isLegacySiteEnabled(siteId: number | string): boolean {
    const state = this.legacySiteState();
    return state !== null && FeatureFlagService.evaluate(state.flag, state.siteOrganizations.get(Number(siteId)));
  }

  /**
   * Attend la réponse de l'API avant de trancher — indispensable dans un guard,
   * où l'arrivée directe par URL précède le chargement des flags.
   */
  resolveLegacySiteEnabled(siteId: number | string): Observable<boolean> {
    return this.legacySiteState$.pipe(
      take(1),
      map(state => FeatureFlagService.evaluate(state.flag, state.siteOrganizations.get(Number(siteId)))),
    );
  }

  /**
   * Un flag actif globalement vaut pour tout le monde ; sinon il ne vaut que
   * pour les organisations listées dans la colonne `organization`.
   * Flag absent, site inconnu ou API en erreur : désactivé.
   */
  private static evaluate(flag: FeatureFlag | undefined, organizationId: number | undefined): boolean {
    if (!flag) { return false; }
    if (flag.isEnabled) { return true; }

    const allowed = flag.organization
      ?.split(',')
      .map(orgId => orgId.trim())
      .filter(orgId => orgId.length > 0) ?? [];

    if (allowed.length === 0 || organizationId === undefined) { return false; }

    return allowed.includes(String(organizationId));
  }
}
